'use client';

import { useState } from 'react';
import type { HomeValuationBlockProps } from '../types';
import { theme, sectionColors } from '../theme';

/**
 * Home-valuation lead capture. CLIENT component (unlike ContactForm, which is a
 * plain server-rendered POST) because it shows the returned estimate inline
 * without a full-page navigation.
 *
 * On submit it POSTs JSON to the tenant site's same-origin `/api/valuation`
 * route, which forwards to the gateway's POST /valuation and returns
 * `{ id, estimate: { low, high, perSqft, comps } | null }`. The lead/valuation
 * destination is resolved server-side per tenant — never hardcoded in the block
 * (CLAUDE.md non-negotiable #2). On success the visitor sees an estimate range
 * (when available) plus an agent-follow-up note; on failure, a retry message.
 */

interface ValuationEstimate {
  low: number;
  high: number;
  perSqft: number;
  comps: number;
}

interface ValuationResponse {
  id?: string;
  estimate?: ValuationEstimate | null;
}

type Status = 'idle' | 'pending' | 'success' | 'error';

export function HomeValuationBlock(props: HomeValuationBlockProps) {
  const {
    heading = "What's your home worth?",
    subheading,
    buttonText = 'Get my estimate',
    showPhone = true,
  } = props;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const field = 'w-full rounded-md border border-black/15 bg-transparent px-3 py-2';

  const [status, setStatus] = useState<Status>('idle');
  const [estimate, setEstimate] = useState<ValuationEstimate | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'pending') return;
    setStatus('pending');

    const form = e.currentTarget;
    const fd = new FormData(form);
    const sqftRaw = String(fd.get('sqft') ?? '').trim();
    const sqft = sqftRaw ? Number(sqftRaw) : undefined;

    const payload: {
      address: string;
      name: string;
      email: string;
      phone: string;
      sqft?: number;
    } = {
      address: String(fd.get('address') ?? '').trim(),
      name: String(fd.get('name') ?? '').trim(),
      email: String(fd.get('email') ?? '').trim(),
      phone: String(fd.get('phone') ?? '').trim(),
    };
    if (sqft !== undefined && Number.isFinite(sqft)) payload.sqft = sqft;

    try {
      const res = await fetch('/api/valuation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = (await res.json()) as ValuationResponse;
      setEstimate(data?.estimate ?? null);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section
      id="home-valuation"
      className="px-6 py-12"
      style={{ ...colors, fontFamily: theme.bodyFont }}
    >
      <div className="mx-auto max-w-xl">
        <h2 className="mb-2 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
        {subheading ? <p className="mb-6 opacity-80">{subheading}</p> : null}

        {status === 'success' ? (
          <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {estimate ? (
              <p className="mb-1 font-medium">
                Homes like yours are worth about ${estimate.low.toLocaleString()}–$
                {estimate.high.toLocaleString()}
              </p>
            ) : null}
            <p>An agent will follow up with your detailed valuation.</p>
          </div>
        ) : (
          <>
            {status === 'error' ? (
              <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                Sorry, something went wrong getting your estimate. Please try again.
              </p>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="address" placeholder="Property address" required className={field} />
              <input type="text" name="name" placeholder="Your name" required className={field} />
              <input type="email" name="email" placeholder="you@example.com" required className={field} />
              {showPhone ? <input type="tel" name="phone" placeholder="Phone (optional)" className={field} /> : null}
              <input
                type="number"
                name="sqft"
                placeholder="Approx. square feet (optional)"
                min={0}
                className={field}
              />
              <button
                type="submit"
                disabled={status === 'pending'}
                className="rounded-md px-6 py-2 font-medium text-white hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: theme.primary }}
              >
                {status === 'pending' ? 'Estimating…' : buttonText}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}
